import React, { useState } from "react";
import Joi from "joi";
import "./new-subscription.m.css";
import { useSelector } from "react-redux";
import get from "lodash/get";
const NewSubscriptionForm = ({ selectedPlanId }) => {
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    address: "",
    zip: "",
    state: "",
    city: "",
    is_gift_subscription: false,
    gift_first_name: "",
    gift_last_name: "",
    gift_phone: "",
    gift_email: "",
  });

  const qtState = useSelector((state) => get(state, ["qt"]));
  const SITE_KEY = get(qtState, ["config", "publisher-attributes", "ccavenue", "google_recaptcha_site_key"], false);

  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const updateField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  // ✅ Joi Validation Schema
  const schema = Joi.object({
    firstName: Joi.string().trim().required().messages({
      "string.empty": "First Name is required",
    }),
    lastName: Joi.string().trim().required().messages({
      "string.empty": "Last Name is required",
    }),
    email: Joi.string().trim().email().required().messages({
      "string.empty": "Email is required",
      "string.email": "Enter a valid email",
    }),
    phone: Joi.string()
      .trim()
      .pattern(/^\d{10}$/)
      .required()
      .messages({
        "string.empty": "Phone is required",
        "string.pattern.base": "Please enter a valid 10-digit number",
      }),
    address: Joi.string().trim().required().messages({
      "string.empty": "Address is required",
    }),
    zip: Joi.string()
      .trim()
      .pattern(/^\d{6}$/)
      .required()
      .messages({
        "string.empty": "Zip/Postal code is required",
        "string.pattern.base": "Postal code must be 6 digits",
      }),
    state: Joi.string().trim().required().messages({
      "string.empty": "State is required",
    }),
    city: Joi.string().trim().required().messages({
      "string.empty": "City is required",
    }),
    is_gift_subscription: Joi.boolean().required(),
    gift_first_name: Joi.string()
      .trim()
      .when("is_gift_subscription", {
        is: true,
        then: Joi.required().messages({ "string.empty": "First Name is required" }),
        otherwise: Joi.allow("").optional(),
      }),
    gift_last_name: Joi.string()
      .trim()
      .when("is_gift_subscription", {
        is: true,
        then: Joi.required().messages({ "string.empty": "Last Name is required" }),
        otherwise: Joi.allow("").optional(),
      }),
    gift_phone: Joi.string()
      .trim()
      .pattern(/^\d{10}$/)
      .when("is_gift_subscription", {
        is: true,
        then: Joi.required().messages({
          "string.empty": "Phone is required",
          "string.pattern.base": "Please enter a valid 10-digit number",
        }),
        otherwise: Joi.allow("").optional(),
      }),
    gift_email: Joi.string()
      .trim()
      .email()
      .when("is_gift_subscription", {
        is: true,
        then: Joi.required().messages({
          "string.empty": "Email is required",
          "string.email": "Enter a valid gift email",
        }),
        otherwise: Joi.allow("").optional(),
      }),
  });

  // ✅ Handle Form Submit
  const handleFormSubmit = async () => {
    setApiError("");
    setSuccessMessage("");

    const { error, value } = schema.validate(form, { abortEarly: false });

    if (error) {
      const formErrors = {};
      error.details.forEach((d) => (formErrors[d.path[0]] = d.message));
      setErrors(formErrors);
      return;
    }

    setErrors({});
    setLoading(true);

    try {
      // ✅ Get reCAPTCHA token
      if (!window.grecaptcha) {
        setApiError("reCAPTCHA not loaded. Please refresh the page and try again.");
        setLoading(false);
        return;
      }

      const token = await window.grecaptcha.execute(SITE_KEY, { action: "submit" });

      // ✅ Prepare payload
      const payload = {
        subscription_package: selectedPlanId,
        first_name: value.firstName,
        last_name: value.lastName,
        phone: value.phone,
        email: value.email,
        address: value.address,
        pin_code: value.zip,
        state: value.state,
        city: value.city,
        is_gift_subscription: value.is_gift_subscription,
        gift_first_name: value.gift_first_name || undefined,
        gift_last_name: value.gift_last_name || undefined,
        gift_phone: value.gift_phone || undefined,
        gift_email: value.gift_email || undefined,
        recaptcha_token: token, // ✅ include reCAPTCHA token
      };

      const response = await fetch("/subscriptions/add", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSuccessMessage(`Subscription created successfully! ID: ${data.id}`);
        setForm({
          firstName: "",
          lastName: "",
          phone: "",
          email: "",
          address: "",
          zip: "",
          state: "",
          city: "",
          is_gift_subscription: false,
          gift_first_name: "",
          gift_last_name: "",
          gift_phone: "",
          gift_email: "",
        });

        // ✅ Redirect to CCAvenue
        const form = document.createElement("form");
        form.method = "POST";
        form.action = data.ccavenue.action_url;

        const inputEnc = document.createElement("input");
        inputEnc.type = "hidden";
        inputEnc.name = "encRequest";
        inputEnc.value = data.ccavenue.encRequest;
        form.appendChild(inputEnc);

        const inputAccess = document.createElement("input");
        inputAccess.type = "hidden";
        inputAccess.name = "access_code";
        inputAccess.value = data.ccavenue.access_code;
        form.appendChild(inputAccess);

        document.body.appendChild(form);
        form.submit();
      } else {
        setApiError(data.message || "Something went wrong");
      }
    } catch (err) {
      console.error(err);
      setApiError("Server error. Please try again later.");
    }

    setLoading(false);
  };

  return (
    <div styleName="form-wrapper">
      <form
        styleName="form"
        onSubmit={(e) => {
          e.preventDefault();
          handleFormSubmit();
        }}
      >
        <div styleName="grid">
          {/* First Name */}
          <div styleName="field">
            <label styleName="label" htmlFor="firstName">
              First Name
            </label>
            <input
              styleName="input"
              id="firstName"
              type="text"
              value={form.firstName}
              onChange={(e) => updateField("firstName", e.target.value)}
            />
            {errors.firstName && <span styleName="error">{errors.firstName}</span>}
          </div>

          {/* Last Name */}
          <div styleName="field">
            <label styleName="label" htmlFor="lastName">
              Last Name
            </label>
            <input
              styleName="input"
              id="lastName"
              type="text"
              value={form.lastName}
              onChange={(e) => updateField("lastName", e.target.value)}
            />
            {errors.lastName && <span styleName="error">{errors.lastName}</span>}
          </div>

          {/* Phone */}
          <div styleName="field">
            <label styleName="label" htmlFor="phone">
              Phone
            </label>
            <input
              styleName="input"
              id="phone"
              type="tel"
              value={form.phone}
              onChange={(e) => updateField("phone", e.target.value.replace(/\D/g, "").slice(0, 10))}
            />
            {errors.phone && <span styleName="error">{errors.phone}</span>}
          </div>

          {/* Email */}
          <div styleName="field">
            <label styleName="label" htmlFor="email">
              Email
            </label>
            <input
              styleName="input"
              id="email"
              type="email"
              value={form.email}
              onChange={(e) => updateField("email", e.target.value)}
            />
            {errors.email && <span styleName="error">{errors.email}</span>}
          </div>

          {/* Address */}
          <div styleName="field">
            <label styleName="label" htmlFor="address">
              Address
            </label>
            <input
              styleName="input"
              id="address"
              type="text"
              value={form.address}
              onChange={(e) => updateField("address", e.target.value)}
            />
            {errors.address && <span styleName="error">{errors.address}</span>}
          </div>

          {/* Zip */}
          <div styleName="field">
            <label styleName="label" htmlFor="zip">
              Zip/Postal Code
            </label>
            <input
              styleName="input"
              id="zip"
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={form.zip}
              onChange={(e) => updateField("zip", e.target.value.replace(/\D/g, "").slice(0, 6))}
            />
            {errors.zip && <span styleName="error">{errors.zip}</span>}
          </div>

          {/* State */}
          <div styleName="field">
            <label styleName="label" htmlFor="state">
              State
            </label>
            <input
              styleName="input"
              id="state"
              type="text"
              value={form.state}
              onChange={(e) => updateField("state", e.target.value)}
            />
            {errors.state && <span styleName="error">{errors.state}</span>}
          </div>

          {/* City */}
          <div styleName="field">
            <label styleName="label" htmlFor="city">
              City
            </label>
            <input
              styleName="input"
              id="city"
              type="text"
              value={form.city}
              onChange={(e) => updateField("city", e.target.value)}
            />
            {errors.city && <span styleName="error">{errors.city}</span>}
          </div>
        </div>

        {/* Gift Subscription */}
        <div styleName="check-box-field">
          <label styleName="checkbox-label">
            <input
              type="checkbox"
              checked={form.is_gift_subscription}
              onChange={(e) => updateField("is_gift_subscription", e.target.checked)}
            />
            <span>Would you like to gift this subscription?</span>
          </label>
        </div>

        <div styleName={`gift-section ${form.is_gift_subscription ? "open" : ""}`}>
          <div styleName="gift-text">Enter Information of the recipient</div>
          <div styleName="grid">
            {/* Gift fields */}
            {["gift_first_name", "gift_last_name", "gift_phone", "gift_email"].map((field) => (
              <div key={field} styleName="field">
                <label styleName="label" htmlFor={field}>
                  {field.replace("gift_", "").replace("_", " ").toUpperCase()}
                </label>
                <input
                  styleName="input"
                  id={field}
                  type={field.includes("email") ? "email" : "text"}
                  value={form[field]}
                  onChange={(e) => updateField(field, e.target.value)}
                />
                {errors[field] && <span styleName="error">{errors[field]}</span>}
              </div>
            ))}
          </div>
        </div>

        {apiError && <div styleName="api-error">{apiError}</div>}
        {successMessage && <div styleName="success">{successMessage}</div>}

        <button styleName="continue" type="submit" disabled={loading}>
          {loading ? "Submitting..." : "Continue"}
        </button>
      </form>
    </div>
  );
};

export default NewSubscriptionForm;
